import get from 'lodash-es/get';
import * as app from '../app/index.js';
import * as auth from '../auth';
import * as router from '../router';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as selectorCreator from '../selector-creator.js';

/**
 * @param { Object } state - Redux state detail object
 * @return { Object } - return currently opened account preferences details.
 */
export const accountPreferences = selectorCreator.default({maxSize: 50})(
  (state) => firestoreRedux.selectors.docsByQueryResult(state, `account-user-preferences-${router.selectors.accountId(state)}-${auth.selectors.currentUserId(state)}`, 'account-user-preferences'),
  (doc) => {
    return doc && doc[0] || undefined;
  }
);

/**
 * The account whose preferences colour the app. Pages scoped to a single account name it in
 * the URL. Home and Views span several accounts, so they name none; a card opened from Views
 * names its own account but renders over the Views page, so that one doesn't count either.
 * Those pages fall back to an account of the user's own, which is what lets them show a
 * colour at all.
 * @param { Object } state - Redux state detail object
 * @return { String } - id of the account whose background color the app shows.
 */
const backgroundColorAccountId = (state) => {
  const viewsPage = router.selectors.pageName(state) === 'VIEWS' || router.selectors.referrerPage(state) === 'VIEWS';
  return (!viewsPage && router.selectors.accountId(state))
    || auth.selectors.currentUserOwnedAccountId(state)
    || auth.selectors.lastUsedAccount(state)
    || get(auth.selectors.accessibleAccounts(state), '[0]');
};

/**
 * @param { Object } state - Redux state detail object
 * @return { Object } - preferences of the account that colours the app.
 */
const backgroundColorPreferences = selectorCreator.default({maxSize: 50})(
  (state) => firestoreRedux.selectors.docsByQueryResult(state, `account-user-preferences-${backgroundColorAccountId(state)}-${auth.selectors.currentUserId(state)}`, 'account-user-preferences'),
  (doc) => {
    return doc && doc[0] || undefined;
  }
);

/**
 * @param { Object } state Redux state detail object
 * @return { String } return account background color based layout.
 */
export const backgroundColor = selectorCreator.reselct.createSelector(
  backgroundColorPreferences,
  app.selectors.wideLayout,
  (preferences, wideLayout) => {
    const backgroundColor = preferences && preferences.backgroundColor;
    return wideLayout ? backgroundColor && backgroundColor.desktop : backgroundColor && backgroundColor.mobile;
  }
);

/**
 * @param {Object} state Redux state
 * @returns `true` when API request to change preferences is in progress.
 */
export const preferencesChangeInProgress = (state) => get(state, 'account-preferences.changePreferences.inProgress');

/**
 * @param {Object} state Redux state
 * @returns {Array} application background color list from application configration.
 */
export const backgroundColorList = selectorCreator.createDeepEqualSelector(
  app.selectors.config,
  (config) => {
    return config && config.bgColors || [];
  }
);